// Public, original articles for aiBLACK — readable without login (SEO + AdSense content).
export const ARTICLES = [
  {
    slug: "building-generational-wealth",
    title: "Building Generational Wealth: A Practical Starter Guide",
    category: "Financial Literacy",
    date: "2026-06-02",
    author: "aiBLACK",
    excerpt:
      "Generational wealth isn't about being rich overnight — it's about building assets, habits, and knowledge that outlive you. Here's where to begin.",
    sections: [
      {
        heading: "What generational wealth really means",
        body: [
          "Generational wealth is any asset passed down from one generation to the next — money, property, businesses, investments, and just as importantly, financial knowledge. For many Black families, decades of redlining, wage gaps, and limited access to credit interrupted that transfer. Closing the gap starts with intentional, repeatable habits far more than with a single lucky break.",
          "The good news: the levers that build lasting wealth are learnable and available to almost anyone who starts early and stays consistent.",
        ],
      },
      {
        heading: "Step 1 — Get your foundation right",
        body: [
          "Before investing, stabilize the base. Build a starter emergency fund of at least $1,000, then work toward three to six months of essential expenses. This is what keeps a flat tire or a medical bill from becoming credit-card debt.",
          "Next, attack high-interest debt aggressively. A credit card at 24% interest is guaranteed to cost you more than most investments will earn, so paying it off is one of the highest-return moves available.",
        ],
      },
      {
        heading: "Step 2 — Make your money grow",
        body: [
          "Once your foundation is set, put time on your side. If your employer offers a 401(k) match, contribute at least enough to capture the full match — that is free money. Beyond that, a Roth IRA lets your investments grow tax-free, which is powerful when you start young.",
          "You don't need to pick individual stocks. Low-cost index funds spread your money across hundreds of companies, reducing risk while capturing the market's long-term growth. Automate a fixed amount every payday so investing happens whether or not you remember.",
        ],
      },
      {
        heading: "Step 3 — Own something",
        body: [
          "Ownership is where wealth compounds. That might be a home, a rental property, or a business. Homeownership builds equity and stability, while a business can create income that isn't tied to a single paycheck.",
          "Ownership also gives you something concrete to pass down. A paid-off home or a profitable side business can change the trajectory of the next generation.",
        ],
      },
      {
        heading: "Step 4 — Protect and pass it on",
        body: [
          "Wealth you can't transfer isn't generational. Write a will, name beneficiaries on every account, and consider term life insurance if others depend on your income. Talk openly with your family about money — silence is how knowledge gets lost.",
          "Start where you are, with what you have. Consistency over decades beats intensity over a few months every single time.",
        ],
      },
    ],
  },
  {
    slug: "support-black-owned-businesses",
    title: "How to Find and Support Black-Owned Businesses",
    category: "Community & Business",
    date: "2026-06-05",
    author: "aiBLACK",
    excerpt:
      "Every dollar you spend is a vote. Here's how to find Black-owned businesses near you and make your support actually count.",
    sections: [
      {
        heading: "Why it matters",
        body: [
          "When money circulates within a community, it multiplies — funding jobs, mentorship, and the next generation of entrepreneurs. Supporting Black-owned businesses is a direct, everyday way to build collective economic power.",
          "It's also about visibility. A thriving local business becomes proof for a young person that ownership is possible.",
        ],
      },
      {
        heading: "How to find them",
        body: [
          "Start with directories and apps built for this purpose — including the Business Directory inside aiBLACK. Search by category and city to discover restaurants, barbers, bookstores, contractors, and professionals near you.",
          "Ask your network, check community social media groups, and look for local Black business expos and markets. Word of mouth remains one of the strongest discovery tools.",
        ],
      },
      {
        heading: "Make your support count",
        body: [
          "Spending is only the beginning. Leave a genuine review — ratings and comments directly influence whether new customers take a chance on a business. Share their page with friends and tag them online.",
          "Buy gift cards during slow seasons, refer them for larger jobs, and be patient with small teams. Loyalty and referrals are worth far more than a single purchase.",
        ],
      },
      {
        heading: "If you're the business owner",
        body: [
          "Claim and complete your listings everywhere you can, with clear photos, hours, and contact info. Respond to reviews — even the critical ones — professionally. Collect emails so you can reach customers directly instead of renting attention from algorithms.",
          "Consistency and reputation compound the same way money does.",
        ],
      },
    ],
  },
  {
    slug: "advocating-for-your-health",
    title: "Closing the Health Gap: How to Advocate for Yourself at the Doctor",
    category: "Health & Wellness",
    date: "2026-06-09",
    author: "aiBLACK",
    excerpt:
      "Studies show Black patients are too often under-listened to. Here's how to walk into an appointment prepared and be heard.",
    sections: [
      {
        heading: "Know the landscape",
        body: [
          "Research has repeatedly documented that Black patients' pain is under-treated and their concerns sometimes dismissed. Knowing this isn't meant to discourage you — it's meant to prepare you to advocate firmly and effectively for the care you deserve.",
          "You are the expert on your own body. A good provider is a partner, not an authority you can't question.",
        ],
      },
      {
        heading: "Before the appointment",
        body: [
          "Write down your symptoms with specifics: when they started, how often, how severe on a 1–10 scale, and what makes them better or worse. Bring a current list of medications and your family history.",
          "Decide your top two or three priorities for the visit so you're not rushed past what matters most. If you can, bring someone you trust to listen and take notes.",
        ],
      },
      {
        heading: "During the appointment",
        body: [
          "State your main concern first and clearly. If you feel unheard, it's okay to say, 'I need you to take this seriously,' or ask, 'What else could this be?' Request that your concern be documented in your chart.",
          "Ask for plain-language explanations, and don't leave until you understand the diagnosis, the plan, and the warning signs that should send you back.",
        ],
      },
      {
        heading: "After the appointment",
        body: [
          "Get copies of your records and test results — you have a right to them. If something feels wrong or unresolved, seek a second opinion without guilt.",
          "Preventive care matters most: regular check-ups, screenings, and managing blood pressure and blood sugar early prevent far larger problems later.",
        ],
      },
    ],
  },
  {
    slug: "juneteenth-history-and-meaning",
    title: "The Roots of Juneteenth and Why It Still Matters",
    category: "History & Culture",
    date: "2026-06-12",
    author: "aiBLACK",
    excerpt:
      "Juneteenth marks the day freedom finally reached the last enslaved people in America. Here's the history — and why it's more than a day off.",
    sections: [
      {
        heading: "What happened on June 19, 1865",
        body: [
          "The Emancipation Proclamation took effect on January 1, 1863, but freedom didn't arrive everywhere at once. It took Union troops reaching the farthest corners of the Confederacy to enforce it. On June 19, 1865 — more than two years later — General Gordon Granger arrived in Galveston, Texas, and announced that all enslaved people were free.",
          "That day became known as Juneteenth, a blend of 'June' and 'nineteenth,' and the oldest nationally recognized commemoration of the end of slavery in the United States.",
        ],
      },
      {
        heading: "From local tradition to national holiday",
        body: [
          "For generations, Juneteenth was celebrated primarily in Texas and Black communities across the country with cookouts, parades, prayer, red foods and drinks, and storytelling. It carried the memory forward even when the wider nation looked away.",
          "In 2021, Juneteenth became a federal holiday — a long-overdue acknowledgment of a history that shaped the entire country.",
        ],
      },
      {
        heading: "Why it still matters",
        body: [
          "Juneteenth is both a celebration and a reckoning. It honors resilience and freedom while reminding us that justice delayed is a pattern, not an accident. It invites reflection on how far there is still to go.",
          "Marking the day — by learning, gathering, supporting Black businesses, or teaching the next generation — keeps the meaning alive rather than reducing it to a date on a calendar.",
        ],
      },
    ],
  },
  {
    slug: "ai-everyday-unfair-advantage",
    title: "Using AI as Your Unfair Advantage: 7 Everyday Ways",
    category: "Technology & Life",
    date: "2026-06-15",
    author: "aiBLACK",
    excerpt:
      "AI isn't just hype — used well, it saves time and levels the playing field. Here are seven practical ways to put it to work today.",
    sections: [
      {
        heading: "Why this is a leverage moment",
        body: [
          "Tools that once required a whole team — a writer, a researcher, a tutor — now fit in your pocket. The people who learn to use them well get more done with less. That's the unfair advantage, and it's available to anyone willing to try.",
          "You don't need to be technical. You just need to know what to ask for.",
        ],
      },
      {
        heading: "7 practical uses",
        body: [
          "1. Write and edit faster — draft emails, cover letters, and social posts, then ask the AI to make them shorter, warmer, or more professional.",
          "2. Understand anything — paste a confusing lease, medical form, or contract and ask for a plain-language summary and the questions you should ask.",
          "3. Plan and budget — turn a goal like 'save for a home in three years' into a step-by-step monthly plan.",
          "4. Learn a skill — get a personalized study plan, practice questions, and explanations tuned to your level.",
          "5. Grow a business — brainstorm names, write product descriptions, and draft marketing calendars.",
          "6. Prep for the moment — rehearse a job interview or a tough conversation and get honest feedback.",
          "7. Stay informed — ask for balanced, sourced summaries of current events instead of scrolling endlessly.",
        ],
      },
      {
        heading: "How to get better answers",
        body: [
          "Be specific: give context, say who it's for, and describe the tone you want. Ask follow-up questions — the first answer is a starting point, not the final word.",
          "Always sanity-check important facts, especially numbers, names, and dates. Used with judgment, AI becomes a force multiplier for everything you're already trying to do.",
        ],
      },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
